import { useState } from "react";
import { Page, Container } from "@/ui/Layout/Page";
import Button from "@/ui/Button";
import { ButtonTypes } from "@/ui/types";
import { Calout } from "@/ui/Calout";
import { Passthrough } from "@/ui/Layout/Passthrough";

// AMB-702, CS-5 (Connector Sign-In v2) — "Connect Claude". One way to connect everywhere: Claude
// opens Launch OS's own sign-in page, the person signs in and clicks Allow, and Claude keeps its
// own renewing key. Nothing here reads or copies the person's Launch OS login (the old Copy code
// did, which is why signing in elsewhere used to disconnect Claude).
export const CONNECTOR_URL = "https://mcp.launchos.com/mcp";
const COMMAND = "/launchos connect";

const CopyRow = ({ text, label }: { text: string; label: string }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setDone(true);
      setTimeout(() => setDone(false), 2000);
    } catch (e) {}
  };
  return (
    <div className="flex items-center gap-2 mt-2">
      <code className="flex-1 rounded-lg bg-black/5 dark:bg-white/10 px-3 py-2 text-xs">{text}</code>
      <Button type={ButtonTypes.DEFAULT} label={done ? "Copied ✓" : label} onClick={copy} />
    </div>
  );
};

/** `embedded`: shown as a Settings tab, so no page frame of its own. */
export const ConnectClaude = ({ embedded = false }: { embedded?: boolean }) => {
  const Shell: any = embedded ? Passthrough : Page;
  const Box: any = embedded ? Passthrough : Container;
  return (
  <Shell {...(embedded ? {} : { topBar: { page: [{ label: "Connect Claude" }] } })}>
    <Box>
      <div className="max-w-2xl flex flex-col gap-6 py-6">
        <div>
          <h2 className="text-xl font-semibold text-black dark:text-white">Connect Claude</h2>
          <p className="text-sm text-black/60 dark:text-white/60 mt-2">
            Let Claude work in Launch OS as you: sites and pages, funnels, CRM, meetings and to-dos, Launch Reviews and
            Launch Inbox, in your own account and the client accounts linked to your agency. Claude asks before it publishes,
            deletes, or replies to anyone.
          </p>
        </div>

        <section className="flex flex-col gap-1 text-sm text-black dark:text-white">
          <h3 className="font-semibold">In Claude (claude.ai and the Claude app)</h3>
          <ol className="list-decimal pl-5 flex flex-col gap-1 text-black/80 dark:text-white/80">
            <li>Open Settings → Connectors → Add custom connector, and paste this address:</li>
          </ol>
          <CopyRow text={CONNECTOR_URL} label="Copy address" />
          <ol start={2} className="list-decimal pl-5 flex flex-col gap-1 mt-2 text-black/80 dark:text-white/80">
            <li>Click Connect. Launch OS's sign-in page opens: sign in and click Allow.</li>
          </ol>
        </section>

        <section className="flex flex-col gap-1 text-sm text-black dark:text-white">
          <h3 className="font-semibold">In Claude Code (the /launchos skill)</h3>
          <div className="text-black/80 dark:text-white/80">Run this once on each computer. Your browser opens the same sign-in page.</div>
          <CopyRow text={COMMAND} label="Copy command" />
        </section>

        <Calout title="Signing in or out of Launch OS won't disconnect Claude, on any computer or in Launch Desktop. A connection only ends after 90 days without use, or when you disconnect it in Claude's settings (or with /launchos disconnect)." />
      </div>
    </Box>
  </Shell>
  );
};
